import { useState } from "react";
import api from "@/lib/api";
import { useSignedFiles } from "@/hooks/useSignedFiles";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanFace, Check, X, Sparkles, ShieldCheck } from "lucide-react";

// Recruiter identity-verification review block — ID + selfie, advisory AI face-match,
// and the human confirm/reject decision (the decision that actually counts).
export function ProctorIdentityBlock({ candId, identity, apiBase, token }) {
  const [sub, setSub] = useState(identity);
  const [busy, setBusy] = useState(false);
  const signUrl = useSignedFiles([sub?.id_path, sub?.selfie_path]);
  if (!sub || (!sub.id_path && !sub.selfie_path)) return null;

  const ai = sub.ai_match;
  const review = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/candidates/${candId}/identity/review`, { decision });
      setSub((s) => ({ ...s, status: decision }));
      toast.success(decision === "confirmed" ? "Identity confirmed" : "Identity rejected");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your review"); }
    finally { setBusy(false); }
  };

  const statusPill = sub.status === "confirmed"
    ? "border-emerald-500/40 text-emerald-300"
    : sub.status === "rejected" ? "border-rose-500/40 text-rose-300" : "border-amber-500/40 text-amber-300";
  const confColor = ai?.confidence == null
    ? "text-muted-foreground"
    : ai.confidence >= 75 ? "text-emerald-300" : ai.confidence >= 45 ? "text-amber-300" : "text-rose-300";

  return (
    <div className="space-y-2.5" data-testid="proctor-identity">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        <ScanFace className="h-3.5 w-3.5" /> Identity verification
        <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${statusPill}`} data-testid="identity-status">{sub.status || "pending"}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {sub.id_path && (
          <figure className="space-y-1">
            <img src={signUrl(sub.id_path)} alt="photo ID" className="aspect-[4/3] w-full object-cover rounded-lg border border-white/10 bg-black" data-testid="identity-id-img" />
            <figcaption className="text-[10px] text-muted-foreground text-center">Photo ID</figcaption>
          </figure>
        )}
        {sub.selfie_path && (
          <figure className="space-y-1">
            <img src={signUrl(sub.selfie_path)} alt="selfie" className="aspect-[4/3] w-full object-cover rounded-lg border border-white/10 bg-black" data-testid="identity-selfie-img" />
            <figcaption className="text-[10px] text-muted-foreground text-center">Live selfie</figcaption>
          </figure>
        )}
      </div>
      {ai ? (
        <div className="rounded-lg border border-border bg-card p-2.5 flex items-start gap-2" data-testid="identity-ai-match">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
          <div className="text-xs">
            <span className="text-muted-foreground">AI face-match (advisory): </span>
            <span className={`font-semibold ${confColor}`}>{ai.confidence != null ? `${ai.confidence}% · ${ai.same_person_likely ? "likely same person" : "possible mismatch"}` : "inconclusive"}</span>
            {ai.note && <div className="text-muted-foreground mt-0.5">{ai.note}</div>}
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground" data-testid="identity-ai-pending">AI face-match is being computed…</div>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => review("confirmed")} data-testid="identity-confirm" className="gap-1.5 flex-1"><Check className="h-3.5 w-3.5 text-emerald-400" /> Confirm</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => review("rejected")} data-testid="identity-reject" className="gap-1.5 flex-1"><X className="h-3.5 w-3.5 text-rose-400" /> Reject</Button>
      </div>
      <p className="text-[10px] text-muted-foreground flex items-start gap-1"><ShieldCheck className="h-3 w-3 text-primary shrink-0 mt-0.5" /> The AI match is advisory only — your decision above is what counts.</p>
    </div>
  );
}
